import { useRef, useState } from "react";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { approveApproval, rejectApproval } from "../../models/board/queriesModel";
import { fetchTaskDetail } from "../../models/shared/taskDetailModel";
import { useBusinessOutcomeFence } from '../../../../features/switchover/viewmodels/useBusinessOutcomeFence';

export function useQuoteApprovalCommands({ qrLoading, actionSnippet, bindingError, canDecide, needsDedicatedCycle, policyLoading, selectedPartsForInjection, approval, userId, isDirty, payload, buildModifiedPayload, manualChannel, onDone, onClose, pricingKey, setReviewCorrection, setPricingNotice, setIsDirty, setKeepAgentText, setTextManuallyEdited, setFullTask, rejectReason, notify }) {
  const { refreshAccess } = useAccess();
  const fence = useBusinessOutcomeFence(userId, 'approval', approval?.id);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const approveFlight = useRef(false);
  const handleApprove = async () => {
    if (fence.outcome || approveFlight.current) return;
    if (qrLoading || !actionSnippet || bindingError) return;
    if (!canDecide || needsDedicatedCycle || policyLoading) return;
    if (selectedPartsForInjection.length === 0) {
      notify.warning("At least one part must be included before approving");
      return;
    }
    approveFlight.current = true;
    setApproving(true);
    try {
      const result = await approveApproval(approval.id, {
        modified_payload: isDirty || payload.pricing_policy_version !== 2 ? buildModifiedPayload() : null,
      });
      if (result?.success !== true) throw Object.assign(new Error(result?.message || 'Approval send outcome could not be confirmed.'), { response: { status: 502, data: result } });
      notify.success(manualChannel ? "Quote approved and recorded as manually shared" : "Quote approved; provider acceptance is not delivery confirmation.");
      onDone?.("approved", approval.id);
      onClose();
    } catch (e) {
      const outcome = fence.capture(e);
      if (outcome) { setPricingNotice(outcome.message); notify.error(outcome.message); return; }
      if (e?.response?.status === 403) refreshAccess?.({ background: true }).catch(() => {});
      const review = e?.response?.data?.data?.pricing_review;
      if (e?.response?.status === 409 && review?.parts) {
        setReviewCorrection({ key: pricingKey, review });
        setPricingNotice("Pricing changed on the server. Review the refreshed figures and message before approving again.");
        setIsDirty(true); setKeepAgentText(false); setTextManuallyEdited(false);
      } else if (e?.response?.status === 409) {
        setPricingNotice(`${e.response.data?.message || "Quote data changed."} Ask the salesperson to reload supplier quotes and submit an updated request.`);
        fetchTaskDetail(payload.query_pk ?? payload.query_id).then(result => { if (result) setFullTask(result); });
      } else {
        notify.error(e?.response?.data?.message || "Failed to approve");
      }
    } finally {
      approveFlight.current = false;
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!canDecide) return;
    if (!rejectReason.trim()) {
      notify.warning("Please enter a reason for rejection");
      return;
    }
    setRejecting(true);
    try {
      await rejectApproval(approval.id, { reason: rejectReason.trim() });
      notify.info("Quote rejected — agent has been notified");
      onDone?.("rejected", approval.id);
      onClose();
    } catch (e) {
      if (e?.response?.status === 403) refreshAccess?.({ background: true }).catch(() => {});
      notify.error(e?.response?.data?.message || "Failed to reject");
    } finally {
      setRejecting(false);
    }
  };


  return { approving, setApproving, rejecting, setRejecting, handleApprove, handleReject, sendOutcome: fence.outcome };
}
